import { BASE_URL } from './inquiries';
import { fetchWithAuth } from './apiClient';

/**
 * cms.contents 검수·발행 — 전부 관리자 전용이다. 초안은 공개 대상이 아니다.
 *
 * 발행 판정 규칙은 **서버에만** 있다. 여기서 다시 계산하지 않고 `/gate` 결과를
 * 그대로 보여 준다 — 같은 규칙을 두 곳에 두면 반드시 어긋나고, 그때 어느 쪽이
 * 맞는지 알 수 없게 된다.
 */
const ROOT = `${BASE_URL}/api/admin/cms/contents`;

const asJson = async (res, fallbackMessage) => {
    const text = await res.text();

    if (!res.ok) {
        let message = fallbackMessage;
        let items = null;
        let needsAcknowledge = false;
        try {
            const body = JSON.parse(text);
            if (body?.message) message = Array.isArray(body.message) ? body.message.join('\n') : body.message;
            items = body?.items ?? null;
            needsAcknowledge = body?.needsAcknowledge === true;
        } catch {
            // JSON 이 아니면 기본 메시지를 쓴다
        }
        const err = new Error(message);
        err.items = items;
        err.needsAcknowledge = needsAcknowledge;
        throw err;
    }

    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch {
        throw new Error('서버 응답을 읽지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
};

const jsonPatch = (body) => ({
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

export const getContents = async ({ type = '', status = '', q = '' } = {}) => {
    const p = new URLSearchParams();
    if (type) p.set('type', type);
    if (status) p.set('status', status);
    if (q) p.set('q', q);
    const qs = p.toString();
    return asJson(await fetchWithAuth(`${ROOT}${qs ? `?${qs}` : ''}`), '목록을 불러오지 못했습니다.');
};

export const getContent = async (id) =>
    asJson(await fetchWithAuth(`${ROOT}/${id}`), '콘텐츠를 불러오지 못했습니다.');

export const getGate = async (id) =>
    asJson(await fetchWithAuth(`${ROOT}/${id}/gate`), '발행 조건을 확인하지 못했습니다.');

export const updateContent = async (id, patch) =>
    asJson(await fetchWithAuth(`${ROOT}/${id}`, jsonPatch(patch)), '저장하지 못했습니다.');

export const replaceFaqs = async (id, faqs) =>
    asJson(await fetchWithAuth(`${ROOT}/${id}/faqs`, jsonPatch({ faqs })), 'FAQ 를 저장하지 못했습니다.');

export const updateMediaAlt = async (id, mediaId, altText) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}/media/${mediaId}/alt`, jsonPatch({ altText })),
        '대체텍스트를 저장하지 못했습니다.',
    );

export const updateMediaRole = async (id, mediaId, role) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}/media/${mediaId}/role`, jsonPatch({ role })),
        '이미지 역할을 바꾸지 못했습니다.',
    );

/** 경고가 남아 있으면 서버가 `needsAcknowledge` 로 거부한다. 확인 후 다시 부른다. */
export const publishContent = async (id, acknowledgeWarnings = false) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}/publish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ acknowledgeWarnings }),
        }),
        '발행하지 못했습니다.',
    );

/** 초안만 지울 수 있다. 발행 중이면 서버가 거부한다 — 먼저 발행 취소. */
export const deleteContent = async (id) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}`, { method: 'DELETE' }),
        '삭제하지 못했습니다.',
    );

export const unpublishContent = async (id) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}/unpublish`, { method: 'POST' }),
        '발행을 취소하지 못했습니다.',
    );

/** 업로드된 이미지를 콘텐츠에 붙인다. 대체텍스트는 등재 시점에 받는다 — 나중에 채우게 두면 `[검수필요]` 가 또 쌓인다. */
export const attachMedia = async (id, payload) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}/media`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }),
        '이미지를 붙이지 못했습니다.',
    );

export const detachMedia = async (id, mediaId) =>
    asJson(
        await fetchWithAuth(`${ROOT}/${id}/media/${mediaId}`, { method: 'DELETE' }),
        '이미지를 떼지 못했습니다.',
    );

// ── 새 콘텐츠 ───────────────────────────────────────────────────────────────
/** 주소는 `draft-…` 로 시작한다. 게이트가 그 형태를 거부하므로 발행 전에 반드시 바꾸게 된다. */
export const createContent = async (payload) =>
    asJson(
        await fetchWithAuth(ROOT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }),
        '콘텐츠를 만들지 못했습니다.',
    );

// ── 저자 (cms.authors) ──────────────────────────────────────────────────────
// 기술노트는 저자가 발행 조건이다(06 N1). 검색과 AI 검색이 "누가 쓴 글인가"를
// 신뢰 신호로 쓰고, Article JSON-LD 의 author 가 비면 리치 결과에서 빠진다.
const AUTHORS = `${BASE_URL}/api/admin/cms/authors`;

export const getAuthors = async () =>
    asJson(await fetchWithAuth(AUTHORS), '저자 목록을 불러오지 못했습니다.');

export const createAuthor = async (payload) =>
    asJson(
        await fetchWithAuth(AUTHORS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }),
        '저자를 추가하지 못했습니다.',
    );

export const updateAuthor = async (id, patch) =>
    asJson(await fetchWithAuth(`${AUTHORS}/${id}`, jsonPatch(patch)), '저자를 수정하지 못했습니다.');

export const setAuthorActive = async (id, isActive) =>
    asJson(
        await fetchWithAuth(`${AUTHORS}/${id}/active`, jsonPatch({ isActive })),
        '저자 상태를 바꾸지 못했습니다.',
    );
